import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Header } from "../../../components/header";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { Textarea } from "../../../components/form";
import { Button } from "../../../components/button";
import { useBulkImportSalesArguments } from "../hooks";

const SAMPLE_PLACEHOLDER = `[
  {
    "theme": "Qu'est-ce que HTTPS et pourquoi c'est important",
    "content": "HTTPS chiffre les échanges entre le site et ses visiteurs..."
  }
]`;

export const ImportSalesArguments: React.FC = () => {
  const navigate = useNavigate();
  const [mutate, { loading }] = useBulkImportSalesArguments();

  const [rawItems, setRawItems] = useState("");
  const [result, setResult] = useState<{ createdCount: number; skippedCount: number } | null>(
    null
  );

  const handleImport = async () => {
    if (loading) return;

    let items;
    try {
      items = JSON.parse(rawItems);
      if (!Array.isArray(items)) throw new Error();
    } catch {
      toast.error("Le contenu collé n'est pas un JSON valide (tableau attendu)");
      return;
    }

    try {
      const { data } = await mutate({ variables: { input: { items } } });

      if (data?.bulkImportSalesArguments?.ok) {
        setResult({
          createdCount: data.bulkImportSalesArguments.createdCount ?? 0,
          skippedCount: data.bulkImportSalesArguments.skippedCount ?? 0,
        });
        toast.success("Import terminé");
      } else {
        toast.error(data?.bulkImportSalesArguments?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  return (
    <>
      <Header
        title="Import d'argumentaires"
        subtitle="Importer un lot d'argumentaires commerciaux"
        buttons={[
          {
            actionText: "Annuler",
            bgColor: "indigo",
            textColor: "white",
            link: `/sales-arguments`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        <div className="card">
          <CardHeader title="Coller le JSON" />
          <Textarea
            className="w-full font-mono text-xs"
            rows={12}
            value={rawItems}
            onChange={(e) => setRawItems(e.target.value)}
            placeholder={SAMPLE_PLACEHOLDER}
          />
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-2">
            Chaque élément attend <code>theme</code> et <code>content</code>. Un argumentaire déjà
            présent avec le même thème sera ignoré, pas dupliqué.
          </p>

          <div className="w-full mt-4">
            <Button onClick={handleImport} loading={loading} disabled={loading || !rawItems}>
              Importer
            </Button>
          </div>
        </div>

        {result && (
          <div className="card mt-4">
            <CardHeader title="Résultat" />
            <div className="flex gap-6">
              <div>
                <p className="text-2xl font-bold text-green-600">{result.createdCount}</p>
                <p className="text-sm text-gray-500 dark:text-zinc-400">Argumentaires créés</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{result.skippedCount}</p>
                <p className="text-sm text-gray-500 dark:text-zinc-400">
                  Ignorés (thème déjà existant)
                </p>
              </div>
            </div>
            <div className="w-full mt-4">
              <Button variant="secondary" onClick={() => navigate("/sales-arguments")}>
                Voir la bibliothèque
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
